import { Link } from "react-router";
import { t, ta } from "../lib/sprache";
import { lesbarBis } from "../model/testende";
import type { LifecycleStatus } from "../pages/agentur/einstellungen/model";
import { VollExport } from "../pages/agentur/einstellungen/Export";
import { TestkontoLoeschen } from "../pages/agentur/einstellungen/TestkontoLoeschen";
import { creatorSperreTexte, exportSatz, sperreSatz, sperreTitel, teamSperreSatz, teamSperreTitel, type SperreLage } from "./sperre-model";

/**
 * Sperrseite nach Vertragsende (Inhaber 22.10.2026, 0425). Bausteine wie die Seiten „Nur lesen“ und „Pausiert“ (nurLesen.tsx):
 * Kasten mit Titel und zwei Sätzen, darunter je ein Kasten für Export, Weitermachen und Löschen. Der Admin exportiert hier direkt
 * (vollständiger Export als ZIP) bzw. über die Listen unter Einstellungen · Export; Teammitglieder lesen, dass der Admin exportiert.
 */

/** Abo & Tarif mit geöffnetem Tarifdialog ohne Vorauswahl */
export const TARIF_WAHL = "/agentur/einstellungen/abo?wechsel=wahl";

export function sperreLage(s: Pick<LifecycleStatus, "reason" | "cancel_effective_at" | "plan" | "reduced_at" | "early_purge_requested_at"> | null | undefined, fmt: (iso: string) => string, trial = false): SperreLage {
  const p = s?.plan ?? null;
  const bis = lesbarBis(p);
  const ende = s?.cancel_effective_at ?? p?.read_only_since ?? null;
  return {
    reason: s?.reason ?? null,
    trial: trial || s?.reason === "trial_expired",
    endeAm: ende ? fmt(ende) : null,
    exportBis: bis ? fmt(bis) : null,
    reduziert: !!(s?.reduced_at ?? p?.reduced_at),
    belegeBis: p?.purge_at ? fmt(p.purge_at) : null,
    allesLoeschen: p?.next_action === "purge",
    loeschungBeantragt: !!s?.early_purge_requested_at,
  };
}

export function AgenturSperre({ orgId, orgName, life, chef, weiter, fmt, trial = false }: {
  orgId: string; orgName: string | null; life: LifecycleStatus | null | undefined;
  /** S-2: nie bezahlt (org_read_only_state.trial): „Dein Test ist beendet“, Testkonto sofort löschbar */
  trial?: boolean;
  /** nur der Admin (Kontoinhaber) exportiert, schließt neu ab und lässt löschen */
  chef: boolean;
  /** Weg zum neuen Abo (bisheriger bzw. empfohlener Tarif); ohne Angabe die Tarifwahl */
  weiter?: { label: string; to: string } | null;
  fmt: (iso: string) => string;
}) {
  const l = sperreLage(life, fmt, trial);
  // S-2 (cc90a030): ein Testkonto ohne aufbewahrungspflichtige Belege ist auch abgelaufen oder gesperrt sofort löschbar
  const testkonto = !!life?.can_delete_trial;
  const loeschen = !!life && (testkonto || life.can_request_early_purge || life.can_withdraw_early_purge);
  return (
    <div className="stack tk-sperre">
      <section className="panel stack" role="status" aria-labelledby="sperre-titel">
        <h1 id="sperre-titel">{chef ? sperreTitel(l.reason, l.trial) : teamSperreTitel(l.reason, l.trial)}</h1>
        {chef ? <><p>{sperreSatz(l)}</p><p>{exportSatz(l)}</p></> : <p>{teamSperreSatz(orgName, l.reduziert ? l.belegeBis : l.exportBis)}</p>}
      </section>
      {chef && (
        <>
          {/* (a) dieselben Funktionen wie unter Einstellungen · Export: vollständiger Export hier, Listen für die Buchhaltung dort */}
          <VollExport orgId={orgId} />
          <section className="panel stack" aria-labelledby="sperre-listen">
            <h2 id="sperre-listen">{t("Listen für die Buchhaltung", "Lists for accounting")}</h2>
            <p>{t("Rechnungen als CSV oder JSON, je nach Land und Tarif auch als DATEV-Buchungsstapel oder BMD-Buchungen, und die Angaben zur Künstlersozialabgabe.",
              "Invoices as CSV or JSON, depending on country and plan also as a DATEV posting batch or BMD bookings, and the details for the artists' social security levy.")}</p>
            <div className="row"><Link className="btn" to="/agentur/einstellungen/export">{t("Listen öffnen", "Open lists")}</Link></div>
          </section>
          {/* (c) neues Abo über den vorhandenen Weg (Tarifdialog unter Abo & Tarif); nach der Reduktion nicht mehr möglich */}
          <section className="panel stack" aria-labelledby="sperre-weiter">
            <h2 id="sperre-weiter">{t("Weitermachen", "Continue")}</h2>
            {l.reduziert ? (
              <p>{ta("Ein neues Abo ist für diese Agentur nicht mehr möglich, weil die Daten schon auf die Belege reduziert sind. Für einen Neustart legst du eine neue Agentur an.",
                "A new subscription is no longer possible for this agency because the data has already been reduced to the records. To start again, create a new agency.", "Ein neues Abo ist für dieses Konto nicht mehr möglich, weil die Daten schon auf die Belege reduziert sind. Für einen Neustart legst du ein neues Konto an.", "A new subscription is no longer possible for this account because the data has already been reduced to the records. To start again, create a new account.")}</p>
            ) : (
              <>
                <p>{t("Mit einem neuen Abo ist sofort alles wieder offen, genau da, wo du aufgehört hast.", "With a new subscription everything is open again right away, exactly where you left off.")}</p>
                <div className="row">
                  <Link className="btn primary" to={weiter?.to ?? TARIF_WAHL}>{weiter?.label ?? t("Abo abschließen", "Subscribe")}</Link>
                  {weiter && <Link className="btn" to={TARIF_WAHL}>{t("Anderen Tarif wählen", "Choose another plan")}</Link>}
                </div>
              </>
            )}
          </section>
          {/* (d) vorzeitig löschen lassen bzw. den Wunsch zurückziehen: unter Abo & Tarif, Abschnitt „Kündigung und Daten“ */}
          {loeschen && testkonto ? (
            <section className="panel stack" aria-labelledby="sperre-loeschen">
              <h2 id="sperre-loeschen">{t("Testkonto löschen", "Delete trial account")}</h2>
              <p>{t("Du brauchst das Testkonto nicht mehr? Dann löschst du es sofort und vollständig; es wird nichts abgebucht. Exportiere vorher, was du behalten möchtest.",
                "You no longer need the trial account? Then delete it right away and completely; nothing is charged. Export anything you want to keep first.")}</p>
              <div className="row"><TestkontoLoeschen orgId={orgId} label={t("Testkonto jetzt löschen", "Delete trial account now")} /></div>
            </section>
          ) : loeschen && (
            <section className="panel stack" aria-labelledby="sperre-loeschen">
              <h2 id="sperre-loeschen">{t("Daten löschen lassen", "Have data deleted")}</h2>
              <p>{life?.early_purge_requested_at
                ? t("Du hast die vorzeitige Löschung beantragt. Bis zum Löschtag kannst du den Wunsch zurückziehen.", "You have requested early deletion. You can withdraw the request until the deletion date.")
                : t("Du brauchst die Daten nicht mehr? Dann lässt du sie vor Ende der Export-Phase löschen: Nach einer Ankündigung per Mail wird alles endgültig gelöscht, auch die aufbewahrten Belege. Vorher bitte alles exportieren.", "You no longer need the data? Then have it deleted before the export phase ends: after a notice by email everything is permanently deleted, including the retained records. Please export everything first.")}</p>
              <div className="row"><Link className="btn" to="/agentur/einstellungen/abo#kuendigung-title">{life?.early_purge_requested_at ? t("Löschwunsch ansehen", "View deletion request") : t("Daten vor Ende der Export-Phase löschen lassen", "Have data deleted before the export phase ends")}</Link></div>
            </section>
          )}
        </>
      )}
      {chef && <p className="muted"><Link to="/agentur/hilfe">{t("Hilfe & Support", "Help & support")}</Link></p>}
    </div>
  );
}

/** Creator einer beendeten Agentur: Bereich der Agentur gesperrt, der eigene Daten-Download unter Konto bleibt (AVV) */
export function CreatorSperre({ orgName, belegeBis, tc }: { orgName: string; belegeBis: string | null; tc: (de: string, en: string) => string }) {
  const x = creatorSperreTexte(orgName, belegeBis, tc);
  return (
    <main id="main" className="content">
      <div className="panel stack tk-empty" role="status">
        <h1 className="tk-state-title">{x.titel}</h1>
        <p>{x.text}</p>
        <div className="row"><Link className="btn primary" to="/creator/konto#konto-daten">{x.knopf}</Link></div>
      </div>
    </main>
  );
}
